import type { ColumnDef } from '@tanstack/react-table'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'

import { Skeleton } from '@/_components/ui/skeleton'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/_components/ui/table'
import { cn } from '@/_lib/utils'

const SKELETON_ROW_COUNT = 12

/**
 * Deterministic pseudo-random width for each skeleton cell,
 * so each row looks organically different without layout shift on re-render.
 */
function getSkeletonWidth(rowIndex: number, colIndex: number): string {
  const widths = ['w-3/4', 'w-2/3', 'w-4/5', 'w-1/2', 'w-5/6', 'w-3/5', 'w-11/12']
  const hash = (rowIndex * 11 + colIndex * 13) % widths.length
  return widths[hash] ?? 'w-3/4'
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  className?: string
  rows?: number
}

export function LoadingDataTable<TData, TValue>({
  columns,
  className,
  rows = SKELETON_ROW_COUNT,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data: Array(rows).fill({}),
    columns: columns.map((column, colIndex) => ({
      ...column,
      cell: ({ row }: { row: { index: number } }) => {
        const width = getSkeletonWidth(row.index, colIndex)

        return (
          <div
            className="flex items-center px-8 py-3"
            style={{ animationDelay: `${row.index * 75}ms` }}
          >
            <Skeleton
              className={cn('h-5 rounded-sm', width)}
              style={{
                animationDelay: `${row.index * 75 + colIndex * 40}ms`,
                animationDuration: '1.8s',
              }}
            />
          </div>
        )
      },
    })),
    getCoreRowModel: getCoreRowModel(),
  })

  const colGroup = (
    <colgroup>
      {table.getAllColumns().map((col) => (
        <col
          key={col.id}
          style={col.columnDef.meta?.width ? { width: col.columnDef.meta.width } : undefined}
        />
      ))}
    </colgroup>
  )

  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-hidden rounded-[8px] border border-brand-grayscale-4',
        className,
      )}
    >
      <div className="h-full overflow-auto">
        <table className="min-w-[768px] w-full table-fixed border-separate border-spacing-0 text-sm">
          {colGroup}
          <TableHeader className="sticky top-0 z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)] [&_tr]:border-0">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-0 bg-brand-grayscale-2">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="h-16 whitespace-nowrap bg-brand-grayscale-2 px-8"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="[&_tr:first-child_td]:pt-1">
            {table.getRowModel().rows.map((row) => (
              <TableRow className="border-0 bg-brand-grayscale-0" key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="border-b border-brand-grayscale-4 p-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>
    </div>
  )
}
